import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Button
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { formatErpMoney } from "../../utils/formatErpMoney";

export const useErpDialogStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 14,
    maxHeight: "min(92vh, 720px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    paddingRight: theme.spacing(0.5)
  },
  titleMeta: {
    color: theme.palette.text.secondary,
    marginTop: 2
  },
  content: {
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(2, 2.5),
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.default
        : "#f8fafc"
  },
  heroCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}14 0%, ${theme.palette.primary.light}08 100%)`,
    border: `1px solid ${theme.palette.primary.main}22`
  },
  heroTitle: {
    fontWeight: 600,
    fontSize: "1.1rem",
    lineHeight: 1.3
  },
  infoCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 10
  },
  fieldLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: 4
  },
  fieldValue: {
    fontSize: "0.95rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    wordBreak: "break-word"
  },
  sectionTitle: {
    fontSize: "0.8rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1)
  },
  linesPaper: {
    borderRadius: 10,
    overflow: "hidden"
  },
  linesScroll: {
    maxHeight: 280,
    overflow: "auto"
  },
  linesTableHead: {
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.grey[800]
        : theme.palette.grey[100]
  },
  totalBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    marginTop: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  actions: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`
  }
}));

export const formatErpDate = (value) => {
  if (!value) return "—";
  const str = String(value).split("T")[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("es-NI", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

export const ErpDialogShell = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  loading,
  children,
  actions,
  maxWidth = "sm"
}) => {
  const classes = useErpDialogStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      scroll="paper"
      classes={{ paper: classes.paper }}
    >
      <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
        <Box className={classes.titleRow}>
          <Box display="flex" alignItems="flex-start" style={{ gap: 12, minWidth: 0 }}>
            {icon}
            <Box minWidth={0}>
              <Typography variant="h6" noWrap>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" className={classes.titleMeta} noWrap>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography color="textSecondary">Cargando…</Typography>
          </Box>
        ) : (
          children
        )}
      </DialogContent>
      {actions && (
        <DialogActions className={classes.actions}>{actions}</DialogActions>
      )}
    </Dialog>
  );
};

export const ErpInfoCard = ({ title, children }) => {
  const classes = useErpDialogStyles();
  return (
    <Paper variant="outlined" className={classes.infoCard} elevation={0}>
      {title && (
        <Typography className={classes.sectionTitle}>{title}</Typography>
      )}
      {children}
    </Paper>
  );
};

export const ErpInfoGrid = ({ fields }) => {
  const classes = useErpDialogStyles();
  return (
    <Grid container spacing={2}>
      {fields.map((f) => (
        <Grid item xs={f.xs || 6} sm={f.sm || f.xs || 6} key={f.label}>
          <Typography className={classes.fieldLabel}>{f.label}</Typography>
          <Typography className={classes.fieldValue}>
            {f.value != null && f.value !== "" ? f.value : "—"}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
};

const getLineName = (line) =>
  line.descripcion ||
  line.producto?.nombre ||
  line.productoNombre ||
  "—";

export const ErpLineItemsTable = ({ lines, currencySymbol }) => {
  const classes = useErpDialogStyles();

  if (!lines || lines.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        Sin líneas de detalle.
      </Typography>
    );
  }

  return (
    <Paper variant="outlined" className={classes.linesPaper} elevation={0}>
      <Typography className={classes.sectionTitle} style={{ padding: "12px 16px 0" }}>
        Artículos ({lines.length})
      </Typography>
      <TableContainer className={classes.linesScroll}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow className={classes.linesTableHead}>
              <TableCell>Producto</TableCell>
              <TableCell align="right" width={72}>
                Cant.
              </TableCell>
              <TableCell align="right" width={96}>
                P. unit.
              </TableCell>
              <TableCell align="right" width={96}>
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, idx) => (
              <TableRow key={line.facturaVentaDetalleId || line.ordenDetalleId || idx}>
                <TableCell>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {getLineName(line)}
                  </Typography>
                  {line.producto?.codigo && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      {line.producto.codigo}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {line.quantity != null ? Number(line.quantity) : "—"}
                </TableCell>
                <TableCell align="right">
                  {line.unitPrice != null
                    ? formatErpMoney(line.unitPrice, currencySymbol)
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {formatErpMoney(line.total || line.subtotal, currencySymbol)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export const ErpTotalBar = ({ label, amount, currencySymbol }) => {
  const classes = useErpDialogStyles();
  return (
    <Box className={classes.totalBar}>
      <Typography variant="subtitle2">{label}</Typography>
      <Typography variant="h6" style={{ fontWeight: 700 }}>
        {formatErpMoney(amount, currencySymbol)}
      </Typography>
    </Box>
  );
};

export const ErpOpenInErpButton = ({ onClick, disabled, label = "Ver en el ERP" }) => (
  <Button
    variant="contained"
    color="primary"
    startIcon={<OpenInNewIcon />}
    onClick={onClick}
    disabled={disabled}
  >
    {label}
  </Button>
);

export const ErpHeroHeader = ({ title, chips, children }) => {
  const classes = useErpDialogStyles();
  return (
    <Paper className={classes.heroCard} elevation={0}>
      <Typography className={classes.heroTitle}>{title}</Typography>
      {chips && (
        <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginTop: 8 }}>
          {chips}
        </Box>
      )}
      {children}
    </Paper>
  );
};
